import { AlertCircle, RefreshCw, Home, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';

interface ErrorDisplayProps {
  title?: string;
  message?: string;
  error?: Error;
  onRetry?: () => void;
  onGoHome?: () => void;
  onContactSupport?: () => void;
  showContactSupport?: boolean;
  variant?: 'default' | 'authentication' | 'network' | 'not-found';
}

export function ErrorDisplay({
  title = 'Something went wrong',
  message = 'We encountered an unexpected error. Please try again.',
  error,
  onRetry,
  onGoHome,
  onContactSupport,
  showContactSupport = true,
  variant = 'default',
}: ErrorDisplayProps) {
  const getVariantConfig = () => {
    switch (variant) {
      case 'authentication':
        return {
          title: 'Session Expired',
          message: 'Your session has expired. Please log in again to continue.',
          primaryAction: {
            label: 'Log in again',
            icon: <Home className="mr-2 h-4 w-4" />,
            onClick: () => window.location.href = '/auth/login',
          },
        };
      case 'network':
        return {
          title: 'Connection Error',
          message: 'Unable to connect to the server. Please check your internet connection.',
          primaryAction: onRetry ? {
            label: 'Retry',
            icon: <RefreshCw className="mr-2 h-4 w-4" />,
            onClick: onRetry,
          } : undefined,
        };
      case 'not-found':
        return {
          title: 'Page Not Found',
          message: 'The page you are looking for does not exist.',
          primaryAction: {
            label: 'Go to homepage',
            icon: <Home className="mr-2 h-4 w-4" />,
            onClick: () => window.location.href = '/',
          },
        };
      default:
        return {
          title,
          message,
          primaryAction: onRetry ? {
            label: 'Try again',
            icon: <RefreshCw className="mr-2 h-4 w-4" />,
            onClick: onRetry,
          } : undefined,
        };
    }
  };

  const config = getVariantConfig();

  return (
    <div className="w-full max-w-md mx-auto">
      <Card className="border-destructive/20">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle className="text-xl">{config.title}</CardTitle>
          <CardDescription className="mt-2">{config.message}</CardDescription>
        </CardHeader>
        
        {error && process.env.NODE_ENV === 'development' && (
          <CardContent>
            <div className="rounded-lg bg-muted p-4 mb-4">
              <p className="text-sm font-mono text-muted-foreground">
                {error.message || 'Unknown error occurred'}
              </p>
              {error.name && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Error: {error.name}
                </p>
              )}
            </div>
          </CardContent>
        )}
        
        <CardFooter className="flex flex-col gap-3">
          {config.primaryAction && (
            <Button 
              onClick={config.primaryAction.onClick} 
              className="w-full"
              variant="default"
            >
              {config.primaryAction.icon}
              {config.primaryAction.label}
            </Button>
          )}
          
          {variant !== 'not-found' && (
            <Button 
              onClick={onGoHome || (() => window.location.href = '/')} 
              className="w-full"
              variant="outline"
            >
              <Home className="mr-2 h-4 w-4" />
              Go to homepage
            </Button>
          )}
          
          {showContactSupport && (
            <Button 
              onClick={onContactSupport || (() => window.location.href = 'mailto:support@rateright.com.au')} 
              className="w-full"
              variant="ghost"
            >
              <Mail className="mr-2 h-4 w-4" />
              Contact support
            </Button>
          )}
        </CardFooter>
      </Card>
      
      <footer className="mt-6 text-center">
        <p className="text-xs text-muted-foreground">
          RateRight — Hire Construction Workers Fast
        </p>
      </footer>
    </div>
  );
}